import {Inject, Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {DOCUMENT} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {GlobalsService} from './globals.service';
import {AuthService} from './auth.service';
import {ImprimirService} from './imprimir.service';
import {IParamsImprimir} from '../interfaces/params-imprimir.interface';
import {ImpresosEnum} from '../enums/imprimir.enum';
import {BehaviorSubject, Subscription, timer} from 'rxjs';

declare let $: any;

@Injectable()
export class TimerNotifyService {

    private generalTimerSubscription: Subscription;
    private appointmentsTimerSubscription: Subscription;
    private signalrTimerSubscription: Subscription;
    private generalTimer;
    private appointmentsTimer;
    private signalrTimer;
    private signalrLoadComplete: BehaviorSubject<boolean> = new BehaviorSubject(null);
    private isSignalrLoaded: boolean = false;
    xqResponse = new BehaviorSubject(null);
    syncAppointmentsResponse = new BehaviorSubject(null);

    private interfacesToCheck = {
        active: false,
        files: [
            { name: 'XQ', minutes: 5 }
        ]
    };

    private renderer: Renderer2;

    constructor(
        private _httpClient: HttpClient,
        @Inject(DOCUMENT) private _document: Document,
        private _rendererFactory: RendererFactory2,
        private _toastr: ToastrService,
        private _globalsService: GlobalsService,
        private _authService: AuthService,
        private _printService: ImprimirService
    ) {
        this.renderer = _rendererFactory.createRenderer(null, null);
        this.generalTimer = timer(0, 300000);
        this.appointmentsTimer = timer(0, 300000);
        this.signalrTimer = timer(0, 5000);

        // Debemos cargar los scripts para signalR en el orden correcto
        this.loadScript('/scripts/jquery.signalR-1.2.2.js', () => {
            this.loadScript('/signalr/hubs', () => {
                this.signalSubscribe();
                this.signalrLoadComplete.next(true);
            });
        });
    }

    /**
     * Carga un nuevo javascript dada una url
     *
     * @param src url del script
     * @param onload funcion a ejecutar terminada la carga
     */
    private loadScript(src: string, onload: Function) {
        console.debug('Load script', src);
        const script = this.renderer.createElement('script');
        script.onload = onload;
        script.type = 'text/javascript';
        script.src = src;
        this.renderer.appendChild(this._document.body, script);
    }

    private signalSubscribe() {
        const signalR = $.connection.DefaultCommandHub;
        // Definimos lo que se va a hacer cuando llegue un mensaje desde servidor
        signalR.client.broadcastMessage = message => {
            const decodedMessage = JSON.parse(message);
            console.debug('message', decodedMessage);

            switch (decodedMessage.OperationCode) {
                case 'Advertisment':
                    this.showMessage(decodedMessage);
                    break;
                case 'PrintOR':
                    this.printOR(decodedMessage);
                    break;
                default:
                    console.log('Operacion SignalR no implementada', decodedMessage);
                    break;
            }
        }
    }

    startSignalRConnection() {
        const startConnection = () => {
            // Iniciamos la conexion solo si el estado es desconectado
            if ($.connection.hub.state === 4) {
                // Utilizamos settimeout para evitar un bug en el que la conexion se cancela si se pierde
                // el contexto de la funcion que la llama
                setTimeout(() => {
                    // La libreria tiene un bug en el que el tiempo de la primera conexion se establece cuando carga el script
                    // y no cuando iniciamos la conexion. Establecemos el tiempo de reconexion a infinito para asegurar que no
                    // timeout al conectar por primera vez
                    $.connection.hub.reconnectWindow = Infinity;
                    $.connection.hub.logging = true;
                    $.connection.hub.start().done(() => {
                        // Una vez creada la conexion podemos volver a poner el tiempo estandar de timeout
                        $.connection.hub.reconnectWindow = 30000;

                        // Como aveces se aborta sola la conexion dejamos un temporizador puesto que detecte si el estado es
                        // desconectado y reinicie la conexion
                        if (!this.signalrTimerSubscription) {
                            this.signalrTimerSubscription = this.signalrTimer.subscribe(_ => {
                                if ($.connection.hub.state === 4) {
                                    this.startSignalRConnection();
                                }
                            });
                        }
                    }).fail(error => {
                        console.error(error);
                    });
                }, 0);
            }
        }

        // Debemos comprobar si ya se ha cargado la libreria de signalR ya que es posible que la aplicacion cargue e
        // inicie la conexion antes de que se haya cargado la libreria
        if (this.isSignalrLoaded) {
            startConnection();
        } else {
            this.signalrLoadComplete.subscribe(isLoaded => {
                if (isLoaded) {
                    this.isSignalrLoaded = true;
                    startConnection();
                }
            });
        }
    }

    closeSignalRConnection() {
        // Paramos el temporizador de desconexion y cerramos conexion informando al servidor de que aborte
        if (this.signalrTimerSubscription) {
            this.signalrTimerSubscription.unsubscribe()
            this.signalrTimerSubscription = null;
        }
        $.connection.hub.stop(false, true);
    }

    private showMessage(message: any) {
        const options = {closeButton: true, disableTimeOut: true, onActivateTick: true};
        switch (message.Importance) {
            case '1': default:
                this._toastr.info(message.Message, '', options);
                break;
            case '2':
                this._toastr.success(message.Message, '', options);
                break;
            case '9':
                this._toastr.warning(message.Message, '', options);
                break;
            case '10':
                this._toastr.error(message.Message, '', options);
                break;
        }

    }

    printOR(message: any) {
        const userProfile = this._authService.getAplicationType();
        if (userProfile.IDAPVPR === message.FLDIDAPVPR) {
            const fecha = this._globalsService.getDateFormatted(message.FLDPRODUCTIONDATA, '"DD/MM/YYYY hh:mm:ss"');
            const params: IParamsImprimir = {
                type: ImpresosEnum.or,
                prefix: message.FLDESPRODUCTIONPREFIX,
                number: message.FLDPRODUCTIONORDER,
                year: fecha.getFullYear()
            }
            this._printService.descargarImpreso(params, null, true);
        }
    }

    startGeneralTimer() {
        if (!this.interfacesToCheck.active) {
            this.interfacesToCheck.active = true;
            this.generalTimerSubscription = this.generalTimer.subscribe(val => this.checkInterfaces(val));
        }
    }

    stopGeneralTimer() {
        if (this.interfacesToCheck.active && this.generalTimerSubscription) {
            this.interfacesToCheck.active = false;
            this.generalTimerSubscription.unsubscribe();
        }
    }

    startAppointmentsTimer() {
        this.appointmentsTimerSubscription = this.appointmentsTimer.subscribe(val => {
            this.syncAppointments();
        });
    }

    stopAppointmentsTimer() {
        if (this.appointmentsTimerSubscription) {
            this.appointmentsTimerSubscription.unsubscribe();
        }
    }

    private checkInterfaces(minutes: number) {
        const files = [];
        for (let i = 0; i < this.interfacesToCheck.files.length; i++) {
            if (minutes % this.interfacesToCheck.files[i].minutes === 0) {
                files.push(this.interfacesToCheck.files[i].name);
            }
        }
        if (files.length > 0) {
            this.checkArrivedInterfaces(files);
        }
    }

    private checkArrivedInterfaces(files: string[]) {
        for (let i = 0; i < files.length; i++) {
            switch (files[i]) {
                case 'XQ':
                    this.searchXQ();
                    break;
                default:
                    break;
            }
        }
    }

    private searchXQ() {
        const _URL_ = `${environment.ENTORNO}Services/Brand/PartsOrderService.svc/GetPendingDocumentsIntegrationEQ0093`;
        const parametros = {};

        this._httpClient.post<any>(_URL_, parametros).subscribe(data => {
            this.xqResponse.next(data);
        });
    }

    private syncAppointments() {
        const _URL_ = `${environment.ENTORNO}${environment.SessionService.PANELCONTROL.TALLER.SyncAppointments}`;
        const parametros = {};
        this._httpClient.post<any>(_URL_, parametros).subscribe(data => {
            this.syncAppointmentsResponse.next(data);
        });
    }

}
